<script setup lang="ts">
/**
 * Sign-in form.
 *
 * FE-014 — rebuilt on the redesign primitives (`FairInputText`, `FairButton`).
 * Validation, the auth call and the modal navigation are unchanged: the PrimeVue
 * fields with their inline `:pt` overrides simply became primitives that already
 * carry the tokens.
 */
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAuthErrors } from '@/composables/useAuthErrors'
import { useAuthModal, useAuthStore } from '@/features/auth/store'
import { FairButton } from '@/ui/primitives/FairButton'
import { FairIcon } from '@/ui/primitives/FairIcon'
import { FairInputText } from '@/ui/primitives/FairInputText'

const emits = defineEmits(['closeModal'])

const { handleAuthError, showSuccessToast } = useAuthErrors()
const { t } = useI18n()

const name = ref('')
const usernameError = ref('')

const valuePassword = ref('')
const passwordError = ref('')
const loadingState = ref(false)
const passwordVisible = ref(false)

const disableSubmit = computed(() => {
  return (
    name.value === ''
    || valuePassword.value === ''
    || !!usernameError.value.length
    || !!passwordError.value.length
  )
})

const validateUsername = () => {
  usernameError.value = name.value.length >= 3 ? '' : t('signup.username_error')
}

const validatePassword = () => {
  passwordError.value = valuePassword.value.length >= 8 ? '' : t('signup.password_error')
}

function forgotPasswordHandler() {
  useAuthModal().showAuthModal('resetPassword')
}

const handleLogin = async () => {
  if (disableSubmit.value || loadingState.value) return
  loadingState.value = true
  try {
    const res = await useAuthStore().auth({
      email: name.value,
      password: valuePassword.value,
    })

    if (res.status === 200) {
      showSuccessToast('Добро пожаловать!', 'Успешный вход')
      useAuthModal().hideAuthModal()
      emits('closeModal')
    }
  } catch (error) {
    handleAuthError(error, 'Login')
  } finally {
    loadingState.value = false
  }
}
</script>

<template>
  <form
    class="fair-auth-form"
    @submit.prevent="handleLogin"
  >
    <div class="fair-auth-form__field">
      <FairInputText
        v-model="name"
        input-id="auth-signin-name"
        :placeholder="t('signup.username_label')"
        :aria-label="t('signup.username_label')"
        :invalid="!!usernameError"
        @input="validateUsername"
        @blur="validateUsername"
      />
      <p
        v-if="usernameError"
        class="fair-auth-form__error"
      >
        {{ usernameError }}
      </p>
    </div>

    <div class="fair-auth-form__field">
      <!-- The primitive hard-codes `type="text"`, so the password field rides in
           its slot: same shell and tokens, real `type="password"`. -->
      <FairInputText :invalid="!!passwordError">
        <input
          id="auth-signin-password"
          v-model="valuePassword"
          class="fair-input-text__control fair-text-input"
          :type="passwordVisible ? 'text' : 'password'"
          :placeholder="t('signup.password_label')"
          autocomplete="current-password"
          @input="validatePassword"
          @blur="validatePassword"
        >
        <button
          type="button"
          class="fair-auth-form__reveal"
          :aria-label="passwordVisible ? t('signup.hide_password') : t('signup.show_password')"
          @click="passwordVisible = !passwordVisible"
        >
          <FairIcon
            :name="passwordVisible ? 'hide' : 'show'"
            :size="20"
            color="currentColor"
          />
        </button>
      </FairInputText>
      <p
        v-if="passwordError"
        class="fair-auth-form__error"
      >
        {{ passwordError }}
      </p>
    </div>

    <div class="fair-auth-form__link-row">
      <button
        type="button"
        class="fair-auth-form__link"
        @click="forgotPasswordHandler"
      >
        {{ t('reset_password.forgot_password') }}
      </button>
    </div>

    <FairButton
      class="fair-auth-form__submit"
      type="submit"
      variant="primary"
      size="m"
      :rounded="false"
      :label="t('signup.sign_in_button')"
      :disabled="disableSubmit || loadingState"
    />
  </form>
</template>
